import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Sun, Zap, Globe, ArrowRight } from 'lucide-react';

const ASU: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Arizona State";

    return (
        <div className="min-h-screen bg-white font-sans text-[#191919] selection:bg-[#8C1D40] selection:text-white">
            {/* Top Bar - Maroon */}
            <div className="bg-[#8C1D40] text-white py-1 px-6 flex justify-end gap-6 text-[11px] font-bold uppercase tracking-wider">
                <a href="#" className="hover:underline">My {schoolName}</a>
                <a href="#" className="hover:underline">Colleges and Schools</a>
                <a href="#" className="hover:underline">Map</a>
                <a href="#" className="hover:underline">Directory</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-100 shadow-sm sticky top-0 z-50">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <img src="https://www.asu.edu/asuthemes/5.0/assets/asu_logo.png" alt="ASU Logo" className="h-10 opacity-90" />
                         {/* Fallback layout if no image */}
                         <div className="hidden">
                            <span className="font-black text-2xl text-[#8C1D40] uppercase">{schoolName}</span>
                         </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#191919] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#8C1D40] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FFC627]">Admissions</a>
                        <a href="#" className="hover:text-[#8C1D40] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FFC627]">Degree Search</a>
                        <a href="#" className="hover:text-[#8C1D40] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FFC627]">Academics</a>
                        <a href="#" className="hover:text-[#8C1D40] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FFC627]">Research</a>
                        <a href="#" className="hover:text-[#8C1D40] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#FFC627]">About</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-[#191919] hover:text-[#8C1D40]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-[#191919]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - #1 in Innovation */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1576402097950-8c20531c360c?q=80&w=2070&auto=format&fit=crop" 
                    alt="Palm Walk" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-3xl mx-auto text-center text-white">
                         <h1 className="text-5xl md:text-8xl font-black mb-6 uppercase italic">
                             #1 in the U.S. <br/> for Innovation.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-bold max-w-2xl mx-auto">
                             Ahead of MIT and Stanford. For the 10th consecutive year.
                         </p>
                         <div className="flex justify-center gap-4">
                             <Button className="bg-[#8C1D40] hover:bg-[#60132b] text-white font-bold uppercase tracking-widest px-10 h-14 rounded-full shadow-lg">
                                 Apply Now
                             </Button>
                             <Button className="bg-[#FFC627] hover:bg-[#e0ad1f] text-black font-bold uppercase tracking-widest px-10 h-14 rounded-full shadow-lg">
                                 Request Info
                             </Button>
                         </div>
                     </div>
                </div>
            </header>

            {/* Stats / Solar */}
            <section className="bg-white py-24 px-6 md:px-12">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="p-8">
                         <Zap size={64} className="text-[#FFC627] mx-auto mb-6" />
                         <h3 className="text-3xl font-black text-[#8C1D40] mb-2">#1</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">In Sustainability</p>
                     </div>
                      <div className="p-8">
                         <Globe size={64} className="text-[#FFC627] mx-auto mb-6" />
                         <h3 className="text-3xl font-black text-[#8C1D40] mb-2">Top 10</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">For International Students</p>
                     </div>
                      <div className="p-8">
                         <Sun size={64} className="text-[#FFC627] mx-auto mb-6" />
                         <h3 className="text-3xl font-black text-[#8C1D40] mb-2">300+</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">Days of Sunshine</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-[#f2f2f2] py-24 px-6 md:px-12">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-gray-300 pb-4">
                         <h2 className="text-3xl font-black text-[#191919] uppercase">{schoolName} News</h2>
                         <a href="#" className="font-bold text-[#8C1D40] text-xs uppercase hover:underline mb-2">Read More</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                         {[
                             "Space instrument built at ASU launches to asteroid belt",
                             "New master's degree in AI launches this fall",
                             "Thunderbird School of Global Management opens new HQ",
                             "Sun Devil stadium renovations complete"
                         ].map((title, i) => (
                             <div key={i} className="bg-white shadow-sm rounded-lg overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow">
                                 <div className="h-48 bg-gray-200 overflow-hidden relative">
                                      <div className="absolute top-0 right-0 bg-[#FFC627] text-black font-bold uppercase text-[10px] px-2 py-1 z-10">Science</div>
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+130}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="News"/>
                                 </div>
                                 <div className="p-6">
                                     <h3 className="text-lg font-bold text-[#191919] group-hover:text-[#8C1D40] transition-colors mb-4 line-clamp-3">{title}</h3>
                                      <div className="flex items-center gap-2 text-[#8C1D40] font-bold uppercase text-xs tracking-widest group-hover:gap-3 transition-all">
                                         Full Story <ArrowRight size={14}/>
                                     </div>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#191919] text-gray-400 py-16 px-6 md:px-12 border-t-[8px] border-[#FFC627]">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <div className="flex items-center gap-2 mb-6">
                               <div className="bg-[#8C1D40] text-white font-bold p-1">{schoolName}</div>
                               <span className="font-bold text-white uppercase text-lg">{schoolName} University</span>
                           </div>
                          <p className="mb-6 opacity-80">
                              Tempe, AZ 85281 <br/>
                              (480) 965-9011
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold text-gray-400 text-xs uppercase tracking-wide">
                          <ul className="space-y-4 hover:[&_a]:text-white">
                              <li><a href="#">Copyright & Trademark</a></li>
                              <li><a href="#">Accessibility</a></li>
                              <li><a href="#">Privacy</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-white">
                               <li><a href="#">Jobs</a></li>
                              <li><a href="#">Emergency</a></li>
                              <li><a href="#">Contact {schoolName.split(' ')[0]}</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-gray-600 border-t border-gray-800 pt-8">
                      © 2026 Arizona Board of Regents
                  </div>
             </footer>
        </div>
    );
};

export default ASU;
